import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Badge,
  Select,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Tag,
  Spinner,
  Center,
  Button,
} from "@chakra-ui/react";
import { HelmetComponent } from "../../components/HelmetComponent";
import { PublisherAndUserContext } from "../../contexts/PublisherAndUserContext";
import moment from "moment";
import { useQuery, useLazyQuery, NetworkStatus } from "@apollo/client";
import { ENGAGEMENT_SUMMARY, ENGAGEMENT_COURSES, ENGAGEMENT_COURSE_DETAIL } from "../../graphql/queries";
import { LoadingOverlay } from "../../components/LoadingOverlay";

const formatMinutes = (m) => {
  const hrs = Math.floor(m / 60);
  const mins = Math.round(m % 60);
  if (hrs <= 0) return `${mins}m`;
  return `${hrs}h ${mins}m`;
};

// Removed mock data. Now wired to GraphQL.

const MiniBars = ({ bars }) => (
  <Flex align="end" mt={2} h="40px">
    {bars.map((h, i) => (
      <Box key={i} w="6px" h={`${h}%`} bg="blue.200" mx="2px" borderRadius="2px" _dark={{ bg: "blue.300" }} />
    ))}
  </Flex>
);

const barsForRange = (range) => {
  if (range === "90d") return [12, 22, 18, 28, 34, 40, 45, 30, 38, 50, 44, 36];
  if (range === "30d") return [18, 26, 32, 24, 30, 36, 28, 34, 40, 29, 33, 37];
  return [28, 46, 38, 52, 35, 57, 44];
};

export const EngagementPage = () => {
  const { user, publisher } = useContext(PublisherAndUserContext);
  const [range, setRange] = useState("7d");
  const bars = useMemo(() => barsForRange(range), [range]);

  const [skip, setSkip] = useState(0);
  const [take] = useState(3);

  const rangeToWindow = (range) => {
    const to = moment();
    const from = range === "90d" ? moment().subtract(90, 'days') : range === "30d" ? moment().subtract(30, 'days') : moment().subtract(7, 'days');
    return { from: from.toISOString(), to: to.toISOString() };
  };

  const windowVars = useMemo(() => rangeToWindow(range), [range]);

  const { data: summaryRes, loading: summaryLoading } = useQuery(ENGAGEMENT_SUMMARY, {
    variables: { publisherId: publisher?.id, from: windowVars.from, to: windowVars.to },
    skip: !publisher?.id,
    fetchPolicy: 'cache-and-network',
  });

  const { data: coursesRes, loading: coursesLoading, networkStatus, fetchMore, refetch } = useQuery(ENGAGEMENT_COURSES, {
    variables: { publisherId: publisher?.id, from: windowVars.from, to: windowVars.to, skip, take },
    skip: !publisher?.id,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    setSkip(0);
    if (publisher?.id) {
      refetch && refetch({ publisherId: publisher.id, from: windowVars.from, to: windowVars.to, skip: 0, take });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, publisher?.id]);

  const [loadCourseDetail] = useLazyQuery(ENGAGEMENT_COURSE_DETAIL, {
    // First load from network for the active window, then reuse cache
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const [courseDetails, setCourseDetails] = useState({});
  const [detailLoadingMap, setDetailLoadingMap] = useState({});

  const onOpenCourse = async (productId) => {
    if (courseDetails[productId] || detailLoadingMap[productId]) return;
    setDetailLoadingMap((prev) => ({ ...prev, [productId]: true }));
    try {
      const res = await loadCourseDetail({ variables: { productId, from: windowVars.from, to: windowVars.to } });
      const detail = res?.data?.engagementCourseDetail;
      if (detail) setCourseDetails((prev) => ({ ...prev, [productId]: detail }));
    } finally {
      setDetailLoadingMap((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const totalActive = summaryRes?.engagementSummary?.activeLearners || 0;
  const avgSessionMins = Math.round((summaryRes?.engagementSummary?.avgSessionSeconds || 0) / 60);
  const totalCompletions = summaryRes?.engagementSummary?.totalCompletions || 0;

  const items = coursesRes?.engagementCourses?.items || [];
  const totalCount = coursesRes?.engagementCourses?.totalCount || 0;
  const canLoadMore = items.length < totalCount;
  const loadMore = () => {
    const nextSkip = items.length;
    setSkip(nextSkip);
    fetchMore && fetchMore({ variables: { skip: nextSkip, take } });
  };

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;
  const showOverlay = (summaryLoading || (coursesLoading && !isLoadingMore));

  return (
    <>
      <HelmetComponent title={`Engagement - Home - ${user?.firstName} ${user?.lastName}`} />

      <Box>
        {showOverlay && <LoadingOverlay loadingMessage="Loading engagement data..." />}
        <Box>
          <Flex align="center" justify="space-between" mb={4}>
            <Flex align="center"><Heading as="h2" size="md">Engagement</Heading></Flex>
            <Flex align="center">
              <Select size="sm" width="140px" value={range} onChange={(e) => setRange(e.target.value)}>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </Select>
            </Flex>
          </Flex>

          {/* Overview */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
            <Stat p={4} borderWidth="1px" borderRadius="md">
              <StatLabel>Active Learners</StatLabel>
              <StatNumber>{totalActive}</StatNumber>
              <StatHelpText>Unique learners with activity in range</StatHelpText>
              <MiniBars bars={bars} />
            </Stat>
            <Stat p={4} borderWidth="1px" borderRadius="md">
              <StatLabel>Avg Session</StatLabel>
              <StatNumber>{formatMinutes(avgSessionMins)}</StatNumber>
              <StatHelpText>Average time spent per session</StatHelpText>
              <MiniBars bars={bars} />
            </Stat>
            <Stat p={4} borderWidth="1px" borderRadius="md">
              <StatLabel>Total Completions</StatLabel>
              <StatNumber>{totalCompletions}</StatNumber>
              <StatHelpText>Course completions in range</StatHelpText>
              <MiniBars bars={bars} />
            </Stat>
          </SimpleGrid>

          <Box>
            <Heading as="h3" size="sm" mb={3}>Per Course Engagement</Heading>
            <Text color="gray.600" mb={4}>
              View average completion, completions count, and activity down to each EdDoc. Video shows watch time, reading shows time spent, assessments show practice count and average score.
            </Text>

            <Accordion allowMultiple>
              {items.map((course) => (
                <AccordionItem key={course.productId} borderWidth="1px" borderRadius="md" mb={3}>
                  <h2>
                    <AccordionButton onClick={() => onOpenCourse(course.productId)}>
                      <Flex flex="1" textAlign="left" align="center" justify="space-between">
                        <Box>
                          <Text fontWeight="600">{course.productName || course.productId}</Text>
                          <Text fontSize="sm" color="gray.600">
                            Avg complete: {Math.min(100, Math.round((course.avgCompletionPct || 0) * 100))}% • Completions: {course.completionsCount || 0}
                          </Text>
                        </Box>
                        <AccordionIcon />
                      </Flex>
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    {detailLoadingMap[course.productId] && (
                      <Center py={6}>
                        <Spinner thickness='3px' speed='0.65s' emptyColor='gray.200' color='blue.500' size='lg' />
                      </Center>
                    )}
                    {(courseDetails[course.productId]?.modules || []).map((mod) => (
                      <Box key={mod.moduleId} p={3} borderWidth="1px" borderRadius="md" mb={3}>
                        <Flex align="center" justify="space-between" mb={2}>
                          <Text fontWeight="600">{mod.moduleName || mod.moduleId}</Text>
                          <Text fontSize="sm" color="gray.600">Avg complete: {Math.min(100, Math.round((mod.avgCompletionPct || 0) * 100))}%</Text>
                        </Flex>
                        <Divider mb={2} />
                        {mod.edDocs.map((dyno) => {
                          const isVideo = (dyno.dynoTypeName || '').toLowerCase().includes('video')
                          const isAssessment = (dyno.practiceCount || 0) > 0 || (dyno.avgScore || 0) > 0
                          const isReading = !isVideo && !isAssessment
                          return (
                          <Flex key={dyno.dynoId} align="center" justify="space-between" p={2} _hover={{ bg: "gray.50" }}>
                            <Flex align="center">
                              <Text>{dyno.dynoTitle || dyno.dynoId}</Text>
                              {isVideo && <Tag ml={3} colorScheme="blue">Video</Tag>}
                              {isReading && <Tag ml={3} colorScheme="green">Reading</Tag>}
                              {isAssessment && <Tag ml={3} colorScheme="purple">Assessment</Tag>}
                            </Flex>
                            <Text fontSize="sm" color="gray.600">
                              {isVideo && <>Watch time: {formatMinutes(Math.round((dyno.watchTimeSeconds || 0) / 60))}</>}
                              {isReading && <>Time spent: {formatMinutes(Math.round((dyno.timeSpentSeconds || 0) / 60))}</>}
                              {isAssessment && <>Practiced: {dyno.practiceCount || 0} • Avg score: {Math.round(dyno.avgScore || 0)}%</>}
                            </Text>
                          </Flex>)
                        })}
                      </Box>
                    ))}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
            {canLoadMore && (
              <Flex mt={4} justify="center">
                <Button onClick={loadMore} colorScheme="blue" isLoading={isLoadingMore} loadingText="Loading more">
                  Load more
                </Button>
              </Flex>
            )}
          </Box>

          <Text color="gray.500" fontSize="sm" mt={6}>Additional ideas: drop-off rate per module, median completion time, top engaged content, per-learner drill-down.</Text>
        </Box>
      </Box>
    </>
  );
};
